import { useState, useCallback } from 'react';

/**
 * Appointment booking component.
 * Uses Calendly URL if configured, otherwise a manual date/time form.
 *
 * @param {{ calendlyUrl?: string, onBooked: (data: object) => void, onSkip: () => void }} props
 */
export default function AppointmentBooking({ calendlyUrl, onBooked, onSkip }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [calendlyOpened, setCalendlyOpened] = useState(false);

  const handleCalendly = useCallback(() => {
    window.open(calendlyUrl, '_blank');
    setCalendlyOpened(true);
  }, [calendlyUrl]);

  const handleManualSubmit = useCallback(() => {
    if (!date) return;
    const scheduledAt = time ? `${date}T${time}:00` : `${date}T09:00:00`;
    onBooked({
      scheduledAt,
      bookingMethod: 'manual',
      notes: notes.trim() || null,
    });
  }, [date, time, notes, onBooked]);

  const handleCalendlyDone = useCallback(() => {
    onBooked({
      bookingMethod: 'calendly',
      notes: 'RDV pris via Calendly',
    });
  }, [onBooked]);

  return (
    <div className="space-y-4">
      <h3 className="font-bold text-lg">RDV contrôle de livraison</h3>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
        <p className="text-blue-800 text-sm">Un RDV visio sera planifié pour vérifier que les vélos ont bien été livrés et que les numéros FNUCI correspondent.</p>
      </div>

      {calendlyUrl ? (
        <div className="space-y-3">
          {!calendlyOpened ? (
            <button
              onClick={handleCalendly}
              className="w-full bg-blue-600 text-white rounded-xl py-3 font-medium"
            >
              Ouvrir Calendly
            </button>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
              <p className="text-sm text-blue-800">
                Calendly ouvert dans un nouvel onglet. Le client a-t-il pris RDV ?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleCalendlyDone}
                  className="flex-1 bg-green-600 text-white rounded-xl py-2 font-medium"
                >
                  RDV pris
                </button>
                <button
                  onClick={() => setCalendlyOpened(false)}
                  className="flex-1 border border-gray-300 rounded-xl py-2 text-sm"
                >
                  Reouvrir
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">Planifier un RDV manuellement :</p>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border rounded-xl px-4 py-3"
          />
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full border rounded-xl px-4 py-3"
          />
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optionnel)"
            className="w-full border rounded-xl px-4 py-3 h-20 resize-none"
          />
          <button
            onClick={handleManualSubmit}
            disabled={!date}
            className="w-full bg-blue-600 text-white rounded-xl py-3 font-medium disabled:opacity-40"
          >
            Confirmer le RDV
          </button>
        </div>
      )}

      <button
        onClick={onSkip}
        className="w-full text-gray-500 text-sm underline py-2"
      >
        Passer (pas de RDV)
      </button>
    </div>
  );
}
